import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { useInvestorProfile, useSuitabilityQuestionnaire, useSubmitSuitability } from '../../hooks/useInvestments';
import type { ProfileType, QuestionnaireAnswer } from '../../types/investments';

const profileLabels: Record<ProfileType, string> = {
  CONSERVATIVE: 'Conservador',
  MODERATE: 'Moderado',
  AGGRESSIVE: 'Arrojado',
};

const profileColors: Record<ProfileType, 'success' | 'primary' | 'error'> = {
  CONSERVATIVE: 'success',
  MODERATE: 'primary',
  AGGRESSIVE: 'error',
};

const profileDescriptions: Record<ProfileType, string> = {
  CONSERVATIVE: 'Você prefere segurança e estabilidade. Investimentos de baixo risco são ideais para você.',
  MODERATE: 'Você busca equilíbrio entre segurança e rentabilidade. Aceita algum risco em troca de melhores retornos.',
  AGGRESSIVE: 'Você busca alta rentabilidade e aceita maior volatilidade. Investimentos de maior risco são adequados.',
};

export function InvestorProfile() {
  const navigate = useNavigate();
  const { data: profile, isLoading: loadingProfile } = useInvestorProfile();
  const { data: questionnaire, isLoading: loadingQuestionnaire } = useSuitabilityQuestionnaire();
  const submitSuitability = useSubmitSuitability();

  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [answers, setAnswers] = useState<QuestionnaireAnswer[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);

  const handleAnswer = (questionId: string, answerId: string, score: number) => {
    const newAnswers = [...answers.filter(a => a.questionId !== questionId), { questionId, answerId, score }];
    setAnswers(newAnswers);
    
    if (questionnaire && currentQuestion < questionnaire.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handleSubmit = async () => {
    await submitSuitability.mutateAsync(answers);
    setShowQuestionnaire(false);
    setAnswers([]);
    setCurrentQuestion(0);
  };

  const isProfileExpired = profile && new Date(profile.validUntil) < new Date();

  if (loadingProfile) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Perfil do Investidor</h1>
          <p className="text-gray-500">Descubra seu perfil e invista de acordo</p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/banking/investments')}>
          Ver Investimentos
        </Button>
      </div>

      {/* Current Profile */}
      {profile && !showQuestionnaire && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Seu Perfil</CardTitle>
              {isProfileExpired && (
                <Badge variant="error">Expirado</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center py-6">
              <Badge 
                variant={profileColors[profile.profile]} 
                className="text-lg px-4 py-2"
              >
                {profileLabels[profile.profile]}
              </Badge>
              <p className="mt-4 text-gray-600 max-w-md mx-auto">
                {profileDescriptions[profile.profile]}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Tolerância ao Risco</p>
                <p className="font-medium">{profile.riskTolerance}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Horizonte</p>
                <p className="font-medium">
                  {profile.investmentHorizon === 'SHORT_TERM' && 'Curto Prazo'}
                  {profile.investmentHorizon === 'MEDIUM_TERM' && 'Médio Prazo'}
                  {profile.investmentHorizon === 'LONG_TERM' && 'Longo Prazo'}
                </p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Conhecimento</p>
                <p className="font-medium">
                  {profile.knowledgeLevel === 'BEGINNER' && 'Iniciante'}
                  {profile.knowledgeLevel === 'INTERMEDIATE' && 'Intermediário'}
                  {profile.knowledgeLevel === 'ADVANCED' && 'Avançado'}
                </p>
              </div>
            </div>

            <div className="mt-6 text-center text-sm text-gray-500">
              <p>
                Válido até: {new Date(profile.validUntil).toLocaleDateString('pt-BR')}
              </p>
            </div>

            <div className="flex justify-center mt-6">
              <Button onClick={() => setShowQuestionnaire(true)}>
                {isProfileExpired ? 'Renovar Perfil' : 'Refazer Questionário'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Profile */}
      {!profile && !showQuestionnaire && (
        <Card>
          <CardContent className="py-12 text-center">
            <h2 className="text-xl font-bold mb-2">Descubra seu Perfil de Investidor</h2>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Responda algumas perguntas para identificarmos seu perfil e 
              recomendarmos os melhores investimentos para você.
            </p>
            <Button onClick={() => setShowQuestionnaire(true)}>
              Iniciar Questionário
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Questionnaire */}
      {showQuestionnaire && questionnaire && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Questionário de Suitability</CardTitle>
              <span className="text-sm text-gray-500">
                {currentQuestion + 1} de {questionnaire.length}
              </span>
            </div>
            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${((currentQuestion + 1) / questionnaire.length) * 100}%` }}
              />
            </div>
          </CardHeader>
          <CardContent>
            {loadingQuestionnaire ? (
              <Skeleton className="h-48" />
            ) : (
              <div className="py-4">
                <h3 className="text-lg font-medium mb-6">
                  {questionnaire[currentQuestion].text}
                </h3>
                <div className="space-y-3">
                  {questionnaire[currentQuestion].options.map((option) => {
                    const isSelected = answers.find(
                      a => a.questionId === questionnaire[currentQuestion].id && a.answerId === option.id
                    );
                    return (
                      <button
                        key={option.id}
                        className={`w-full p-4 text-left border-2 rounded-lg transition-colors ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => handleAnswer(
                          questionnaire[currentQuestion].id,
                          option.id,
                          option.score
                        )}
                      >
                        {option.text}
                      </button>
                    );
                  })}
                </div>

                <div className="flex justify-between mt-8">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      if (currentQuestion > 0) {
                        setCurrentQuestion(currentQuestion - 1);
                      } else {
                        setShowQuestionnaire(false);
                      }
                    }}
                  >
                    {currentQuestion > 0 ? 'Anterior' : 'Cancelar'}
                  </Button>
                  
                  {currentQuestion === questionnaire.length - 1 && answers.length === questionnaire.length && (
                    <Button onClick={handleSubmit} disabled={submitSuitability.isPending}>
                      {submitSuitability.isPending ? 'Processando...' : 'Finalizar'}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default InvestorProfile;
